import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ClipboardList } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useFunder } from "@/funder/FunderContext";
import { Input } from "@/components/ui-kit/Input";
import { Card } from "@/components/ui-kit/Card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Pill } from "@/components/ui-kit/Pill";

const TABS = ["All", "ACSA Client", "Client", "Needs Review", "Ready to Invoice", "Unpaid/Partial", "Closed/Archived"];

function Balance({ b }) {
  if (!b) return <span className="text-muted-foreground">—</span>;
  if (b.balance > 0) return <span className="nums font-medium text-foreground">{money(b.balance)}</span>;
  if (b.credit > 0) return <span className="nums text-status-paid">{money(b.credit)} cr</span>;
  return <span className="nums text-muted-foreground">{money(0)}</span>;
}

export function WorkbenchOverviewPage() {
  const navigate = useNavigate();
  const { abbr, typeLabel } = useFunder();
  const tabLabel = (t) => (t === "ACSA Client" ? `${abbr} Client` : t);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("All");

  useEffect(() => {
    api.get("/board").then((r) => setRows(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const matchQ = !q || r.client.name.toLowerCase().includes(q) || (r.matter?.name || "").toLowerCase().includes(q);
      if (!matchQ) return false;
      if (tab === "Closed/Archived") return !r.has_active && r.closed_count > 0;
      if (!r.has_active) return false; // active-matter list by default
      switch (tab) {
        case "ACSA Client": return r.matter.type === "ACSA_Client";
        case "Client": return r.matter.type === "Client";
        case "Needs Review": return r.needs_review_count > 0;
        case "Ready to Invoice": return r.ready_to_invoice;
        case "Unpaid/Partial": return r.unpaid_partial;
        default: return true;
      }
    });
  }, [rows, query, tab]);

  return (
    <div className="space-y-6" data-testid="workbench-overview-page">
      <div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Workbench</h1>
        <p className="mt-2 text-sm text-muted-foreground">Open an active matter to log time and review billing.</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="wbo-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search matters…" className="pl-9" />
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="wbo-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`wbo-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setTab(t)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
              tab === t ? "border-primary/40 bg-primary/10 text-primary" : "border-border bg-surface text-muted-foreground hover:bg-accent"
            )}
          >
            {tabLabel(t)}
          </button>
        ))}
      </div>

      <Card>
        {error ? (
          <div className="p-6 text-sm text-destructive">{error}</div>
        ) : rows === null ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-lg bg-secondary text-muted-foreground"><ClipboardList className="h-6 w-6" /></span>
            <p className="text-sm text-muted-foreground">No matters match this filter.</p>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Client</TH><TH>Active matter</TH><TH>Type</TH><TH>Status</TH>
                <TH className="text-right">Hours</TH><TH className="text-right">Client bal.</TH><TH className="text-right">{abbr} bal.</TH>
                <TH className="text-right">Needs review</TH><TH>Alerts</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((r) => (
                <TR key={r.client.id} data-testid={`wbo-row-${r.client.id}`}>
                  <TD>
                    <button
                      type="button"
                      data-testid={`wbo-open-${r.matter ? r.matter.id : r.client.id}`}
                      onClick={() => navigate(r.matter ? `/workbench/${r.matter.id}` : `/clients-matters/${r.client.id}`)}
                      className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                      {r.client.name}
                    </button>
                  </TD>
                  <TD className="text-muted-foreground">{r.matter?.name || "—"}</TD>
                  <TD className="text-muted-foreground">{r.matter ? typeLabel(r.matter.type) : "—"}</TD>
                  <TD>{r.has_active ? <Pill tone="success">Open</Pill> : <Pill tone="muted">{r.closed_count} closed</Pill>}</TD>
                  <TD className="nums text-right">{r.has_active ? r.hours_approved : "—"}</TD>
                  <TD className="text-right"><Balance b={r.balances?.client} /></TD>
                  <TD className="text-right"><Balance b={r.balances?.acsa} /></TD>
                  <TD className="text-right">{r.needs_review_count > 0 ? <Pill tone="info">{r.needs_review_count}</Pill> : <span className="text-muted-foreground">0</span>}</TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      {r.ready_to_invoice && <Pill tone="accent">Ready</Pill>}
                      {r.unpaid_partial && <Pill tone="muted">Unpaid</Pill>}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
